import {
  MEETING_AUDIO_BUFFER_MS,
  MEETING_CLOSE_ENDED,
  MEETING_CLOSE_NOT_STARTED,
  encodeLiveAudioFrame,
  meetingServerMessageSchema,
  type MeetingEndReason,
  type MeetingParticipant,
  type MeetingServerMessage,
  type MeetingSummary,
  type TranscriptSegment,
  type TranscriptionPauseReason,
  type TranscriptionState,
} from '@hub/contracts'
import { getWsBaseUrl, buildWsRoomPath } from '@/lib/collab/ws-url'

/**
 * A segment as the transcriber produces it: some PCM and how long it is. The
 * socket assigns the `seq`, and the **room** assigns when it happened — a
 * browser's clock is its own (REVIEW-2 #1).
 */
export interface AudioSegment {
  durationMs: number
  pcm: Int16Array
  utteranceId: number
  start: boolean
  end: boolean
}

/**
 * The room socket client (PLAN-085 §5.3, §7).
 *
 * Carries JSON both ways and **binary microphone frames** upward. It
 * reconnects with backoff, and buffers segments produced while the socket is
 * down — up to a minute of audio, because a transcript with a minute-old gap
 * is worth more than one that silently drops everything.
 */

export interface MeetingRoomState {
  status: 'scheduled' | 'live' | 'ended'
  startedAt: string | null
  participants: MeetingParticipant[]
  summary: MeetingSummary | null
  segments: TranscriptSegment[]
  transcription: TranscriptionState
  /** Why `transcription` is 'paused' — the host, or the credits gate. */
  pausedBy: TranscriptionPauseReason | null
  /** A RealtimeKit recording of the meeting is running (board m5gc #8). */
  recording: boolean
  connected: boolean
  endedReason: MeetingEndReason | null
  notesDocumentId: string | null
}

export const EMPTY_ROOM_STATE: MeetingRoomState = {
  status: 'live',
  startedAt: null,
  participants: [],
  summary: null,
  segments: [],
  transcription: 'live',
  pausedBy: null,
  recording: false,
  connected: false,
  endedReason: null,
  notesDocumentId: null,
}

/**
 * Audio held while the socket is down. Shared with the room's ingest slack
 * (`INGEST_SLACK_MS`), which is defined as this plus one segment — otherwise
 * a reconnect's buffer is refused half-drained (REVIEW-2 #3).
 */
export const MAX_BUFFERED_MS = MEETING_AUDIO_BUFFER_MS
const RECONNECT_MIN_MS = 1_000
const RECONNECT_MAX_MS = 10_000
/** The transcript panel keeps this many lines in memory. */
const MAX_SEGMENTS = 500

type Listener = (state: MeetingRoomState) => void

export class MeetingRoomClient {
  #socket: WebSocket | null = null
  #state: MeetingRoomState = { ...EMPTY_ROOM_STATE }
  #listeners = new Set<Listener>()
  #reconnectMs = RECONNECT_MIN_MS
  #reconnectTimer: ReturnType<typeof setTimeout> | null = null
  #closed = false
  #buffer: AudioSegment[] = []
  /**
   * Frame sequence numbers belong to the **connection**, not to whatever
   * produced the audio: the DO drops anything that does not advance, and the
   * transcriber is rebuilt on every mute/unmute (its microphone track
   * changes), so a per-transcriber counter restarted at 1 and every frame was
   * dropped until it caught up (REVIEW-1 #5). Reset on `open`, because the
   * DO's own `lastSeq` is per connection too.
   */
  #seq = 0
  readonly #url: string

  constructor(
    projectId: string,
    meetingId: string,
    private readonly clientId: string
  ) {
    this.#url = `${getWsBaseUrl()}/${buildWsRoomPath(projectId, 'meeting', meetingId)}`
  }

  get state(): MeetingRoomState {
    return this.#state
  }

  subscribe(listener: Listener): () => void {
    this.#listeners.add(listener)
    return () => this.#listeners.delete(listener)
  }

  connect(): void {
    if (this.#closed || this.#socket) return
    const socket = new WebSocket(this.#url)
    socket.binaryType = 'arraybuffer'
    this.#socket = socket

    socket.addEventListener('open', () => {
      // Closed while still connecting (an unmount that beat the handshake —
      // React's dev double-effect does this every time): the browser's
      // close() on a CONNECTING socket does not reach the room, so the
      // connection stayed registered there as a ghost participant until the
      // meeting ended (board m5gc #16). Close it now that it is open.
      if (this.#closed) {
        socket.close(1000, 'left')
        return
      }
      this.#reconnectMs = RECONNECT_MIN_MS
      // A fresh connection means a fresh `lastSeq` in the room.
      this.#seq = 0
      socket.send(JSON.stringify({ t: 'hello', clientId: this.clientId }))
      this.#patch({ connected: true })
      this.#flush()
    })

    socket.addEventListener('message', (event) => {
      if (typeof event.data !== 'string') return
      let message: MeetingServerMessage
      try {
        message = meetingServerMessageSchema.parse(JSON.parse(event.data))
      } catch {
        return
      }
      this.#apply(message)
    })

    socket.addEventListener('close', (event) => {
      this.#socket = null
      this.#patch({ connected: false })
      // A room that ended, or was never started, is not worth reconnecting to.
      if (event.code === MEETING_CLOSE_ENDED || event.code === MEETING_CLOSE_NOT_STARTED) {
        this.#closed = true
        if (event.code === MEETING_CLOSE_ENDED) this.#patch({ status: 'ended' })
        return
      }
      this.#scheduleReconnect()
    })

    socket.addEventListener('error', () => socket.close())
  }

  /**
   * A deliberate goodbye (board fqw7): tells the room to write the leave row
   * now instead of waiting out the reconnect grace, then closes. Only a
   * socket that is open can carry it; the provider's `meetings.leave` call
   * covers the rest.
   */
  leave(): void {
    const socket = this.#socket
    if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify({ t: 'leave' }))
    this.close()
  }

  close(): void {
    this.#closed = true
    if (this.#reconnectTimer) clearTimeout(this.#reconnectTimer)
    this.#reconnectTimer = null
    const socket = this.#socket
    // A socket still connecting is closed by its own 'open' handler (above);
    // closing it here would be silently dropped by the browser.
    if (socket && socket.readyState !== WebSocket.CONNECTING) socket.close(1000, 'left')
    this.#socket = null
    this.#buffer = []
  }

  /**
   * Send one microphone segment. The sequence number is assigned here, so it
   * is continuous across mute cycles and transcriber restarts. While the
   * socket is down the segment is buffered — and numbered on the way out, so
   * a reconnect's frames start from 1 against the room's fresh `lastSeq`.
   */
  sendAudio(segment: AudioSegment): void {
    if (this.#closed) return
    const socket = this.#socket
    if (socket?.readyState === WebSocket.OPEN) {
      socket.send(encodeLiveAudioFrame({ ...segment, seq: ++this.#seq }))
      return
    }
    this.#buffer.push(segment)
    let buffered = this.#buffer.reduce((sum, f) => sum + f.durationMs, 0)
    while (buffered > MAX_BUFFERED_MS && this.#buffer.length > 1) {
      const dropped = this.#buffer.shift()!
      buffered -= dropped.durationMs
    }
  }

  /**
   * Host only — the room refuses it from anyone else. Pausing is the whole
   * room's state, not this tab's: every client stops uploading and the room
   * refuses frames regardless (board m5gc #2).
   */
  setTranscriptionPaused(paused: boolean): void {
    const socket = this.#socket
    if (socket?.readyState !== WebSocket.OPEN) return
    socket.send(JSON.stringify({ t: 'transcription', action: paused ? 'pause' : 'resume' }))
  }

  /**
   * Raise or lower a hand (board m5gc #12). `userId` lowers someone else's —
   * the room accepts that from the host only.
   */
  setHand(raised: boolean, userId?: string): void {
    const socket = this.#socket
    if (socket?.readyState !== WebSocket.OPEN) return
    socket.send(JSON.stringify({ t: 'hand', raised, ...(userId ? { userId } : {}) }))
  }

  #flush(): void {
    const socket = this.#socket
    if (socket?.readyState !== WebSocket.OPEN) return
    const pending = this.#buffer
    this.#buffer = []
    for (const segment of pending) {
      socket.send(encodeLiveAudioFrame({ ...segment, seq: ++this.#seq }))
    }
  }

  #scheduleReconnect(): void {
    if (this.#closed || this.#reconnectTimer) return
    this.#reconnectTimer = setTimeout(() => {
      this.#reconnectTimer = null
      this.#reconnectMs = Math.min(this.#reconnectMs * 2, RECONNECT_MAX_MS)
      this.connect()
    }, this.#reconnectMs)
  }

  #apply(message: MeetingServerMessage): void {
    switch (message.t) {
      case 'state':
        this.#patch({
          status: message.status,
          startedAt: message.startedAt,
          participants: message.participants,
          summary: message.summary,
          segments: message.segments,
          transcription: message.transcription,
          pausedBy: message.pausedBy,
          recording: message.recording,
        })
        break
      case 'participants':
        this.#patch({ participants: message.participants })
        break
      case 'transcript':
        this.#patch({
          segments: appendSegment(this.#state.segments, message.segment),
        })
        break
      case 'summary':
        this.#patch({ summary: message.summary })
        break
      case 'transcription':
        this.#patch({ transcription: message.state, pausedBy: message.pausedBy })
        break
      case 'recording':
        this.#patch({ recording: message.active })
        break
      case 'ended':
        this.#patch({
          status: 'ended',
          endedReason: message.reason,
          notesDocumentId: message.notesDocumentId,
        })
        break
      case 'error':
        console.error('[meet] room error:', message.message)
        break
    }
  }

  #patch(patch: Partial<MeetingRoomState>): void {
    this.#state = { ...this.#state, ...patch }
    for (const listener of this.#listeners) listener(this.#state)
  }
}

/** The wire frame: position is always 0, the header's `endMs` is the duration. */
/**
 * Segments arrive in transcription order, which is *not* the order they were
 * spoken in — a coalesced backlog lands after a later short segment, and two
 * speakers finish out of turn. They are sorted by **when they were said**
 * (the room's clock, REVIEW-2 #1), which is also the order the final
 * transcript uses; `seq` only breaks ties. A repeat — a reconnect replaying
 * the tail — replaces rather than duplicates.
 */
export function appendSegment(
  segments: TranscriptSegment[],
  segment: TranscriptSegment
): TranscriptSegment[] {
  const existing = segments.findIndex((s) => s.seq === segment.seq)
  const next =
    existing >= 0
      ? segments.map((s, i) => (i === existing ? segment : s))
      : [...segments, segment].sort((a, b) => a.startMs - b.startMs || a.seq - b.seq)
  return next.length > MAX_SEGMENTS ? next.slice(next.length - MAX_SEGMENTS) : next
}
